/**
 * fetchClient – axios replacement using the native Fetch API.
 * Reproduces the same response shape as axios:
 *   { data, headers, status }
 * and throws errors with the same shape:
 *   error.response = { data, status, headers }
 * so that errorHandler and all existing code keep working without any changes.
 */
import env from 'variables/config';
import { clearAllFilters } from 'utils/filtersStorage';

const BASE_URL: string = env.apiUrl;

export interface FetchClientResponse<T = any> {
    data: T;
    status: number;
    headers: Record<string, string>;
}

function headersToPlain(headers: Headers): Record<string, string> {
    const out: Record<string, string> = {};
    headers.forEach((value, key) => {
        out[key] = value;
    });
    return out;
}

// Matches the two backend messages that mean "your token isn't valid anymore"
// (store/middleware.ts: TokenExpiredError -> "Votre session a expiré..." /
// no-or-bad-token -> "You need to be logged."). Deliberately does NOT match
// permission-denied 403s ("Vous n'avez pas la permission...") which are a
// valid session that simply lacks a right — those must not log the user out.
const SESSION_DEAD_RE = /session a expir|^you need to be logged\.?$/i;

/**
 * If this request was authenticated with the main app session token (the one
 * stored under 'token' in localStorage) and the backend says that token is
 * dead, clear the session and send the user back to the login screen instead
 * of leaving them stuck replaying the same failing request forever.
 * No-ops for any other auth flow (customer/delivery-person/supplier tokens),
 * since those aren't carried in the 'token' key.
 */
function handleSessionExpiry(errorData: any, reqHeaders: Record<string, string>): void {
    if (typeof window === 'undefined') return;

    const message: string = (errorData?.error || errorData?.message || '').trim();
    if (!SESSION_DEAD_RE.test(message)) return;

    const storedToken = localStorage.getItem('token');
    if (!storedToken) return;

    const authHeader = reqHeaders['Authorization'] || reqHeaders['authorization'];
    if (authHeader !== `Bearer ${storedToken}`) return;

    localStorage.removeItem('token');
    localStorage.removeItem('name');
    localStorage.removeItem('userId');
    localStorage.removeItem('permissions');
    localStorage.removeItem('ownerId');
    clearAllFilters();

    if (!window.location.pathname.startsWith('/auth')) {
        window.location.href = '/auth/login';
    }
}

async function request<T = any>(
    method: string,
    url: string,
    options: {
        data?: any;
        headers?: Record<string, string>;
        responseType?: 'json' | 'blob' | 'arraybuffer' | 'text';
        params?: Record<string, string | number | boolean>;
    } = {},
): Promise<FetchClientResponse<T>> {
    // Build the full URL
    let fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;

    // Handle querystring params (like axios params option)
    if (options.params && Object.keys(options.params).length > 0) {
        const qs = new URLSearchParams();
        Object.entries(options.params).forEach(([k, v]) => {
            if (v !== undefined && v !== null) qs.append(k, String(v));
        });
        fullUrl += (fullUrl.includes('?') ? '&' : '?') + qs.toString();
    }

    const reqHeaders: Record<string, string> = { ...(options.headers ?? {}) };

    let body: BodyInit | undefined;
    if (options.data !== undefined) {
        if (options.data instanceof FormData) {
            // Let the browser set the Content-Type header with the boundary
            body = options.data;
        } else {
            reqHeaders['Content-Type'] = 'application/json';
            body = JSON.stringify(options.data);
        }
    }

    const response = await fetch(fullUrl, {
        method,
        headers: reqHeaders,
        body,
    });

    const plainHeaders = headersToPlain(response.headers);

    if (!response.ok) {
        // Reproduce axios error shape: err.response.data
        let errorData: any;
        if (
            options.responseType === 'blob' ||
            options.responseType === 'arraybuffer'
        ) {
            // For binary responses, decode the error as text
            try {
                const text = await response.text();
                errorData = text ? JSON.parse(text) : { message: response.statusText };
            } catch {
                errorData = { message: response.statusText };
            }
        } else {
            try {
                errorData = await response.json();
            } catch {
                errorData = { message: response.statusText };
            }
        }
        const err: any = new Error(
            errorData?.message || errorData?.error || 'Request failed',
        );
        err.response = {
            data: errorData,
            status: response.status,
            headers: plainHeaders,
        };

        // Auto-logout when the main app session token has actually expired/died.
        // Scoped tightly so it never fires for permission-denied 403s (valid
        // session, missing right) or for other auth flows that don't use the
        // 'token' key (customer/delivery-person/supplier tokens) — those keep
        // whatever handling they already have.
        if (response.status === 401 || response.status === 403) {
            handleSessionExpiry(errorData, reqHeaders);
        }

        throw err;
    }

    // Read the response body according to the requested responseType
    let data: any;
    if (options.responseType === 'blob') {
        data = await response.blob();
    } else if (options.responseType === 'arraybuffer') {
        data = await response.arrayBuffer();
    } else {
        const text = await response.text();
        data = text ? JSON.parse(text) : null;
    }

    return { data, status: response.status, headers: plainHeaders };
}

const fetchClient = {
    get<T = any>(url: string, config: any = {}): Promise<FetchClientResponse<T>> {
        const { headers, responseType, params } = config;
        return request<T>('GET', url, { headers, responseType, params });
    },

    post<T = any>(
        url: string,
        data?: any,
        config: any = {},
    ): Promise<FetchClientResponse<T>> {
        const { headers, responseType, params } = config;
        // Edge case: axios.post(url, config) called without a body (supplier.tsx)
        const body = data !== undefined ? data : config?.data;
        return request<T>('POST', url, { data: body, headers, responseType, params });
    },

    put<T = any>(
        url: string,
        data?: any,
        config: any = {},
    ): Promise<FetchClientResponse<T>> {
        const { headers, responseType, params } = config;
        return request<T>('PUT', url, { data, headers, responseType, params });
    },

    patch<T = any>(
        url: string,
        data?: any,
        config: any = {},
    ): Promise<FetchClientResponse<T>> {
        const { headers, responseType, params } = config;
        return request<T>('PATCH', url, { data, headers, responseType, params });
    },

    /**
     * DELETE – axios.delete(url, config) passes the body through config.data
     */
    delete<T = any>(
        url: string,
        config: any = {},
    ): Promise<FetchClientResponse<T>> {
        const { headers, responseType, params, data } = config;
        return request<T>('DELETE', url, { data, headers, responseType, params });
    },
};

export default fetchClient;
